import { TextField } from "@mui/material";
import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greenMui,
  greyMui,
  MOBILE_BREAKPOINT,
  purpleMui,
  redMui,
  rydeColors,
} from "@ryde/theme";

export const ChatConversationPreviewWrapper = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "chat-conversation-preview-wrapper",
  },
)`
  margin-top: 10px;
  border-radius: 5px;
  border: 1px solid ${rydeColors.softGray};
  background: ${rydeColors.white};
  margin-inline-end: 24px;
  /* Take whatever the input box leaves, rather than a fixed half-viewport: the transcript
     is the content here, and every row it cannot show is one the user has to scroll for. */
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;

  .inner-conversation-wrapper {
    padding: 10px;
    padding-inline-start: 20px;
    line-height: 1.6;
    font-size: 14px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: start;
    /* min-height: 0 lets a flex child actually shrink; without it the scroll container
       grows past its parent instead of scrolling. */
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;

    & > ul {
      padding-inline-start: 20px;
    }

    .assistant-message-block {
      display: flex;
      flex-direction: column;
      align-items: stretch;
    }

    .streaming-cursor {
      display: inline-block;
      width: 2px;
      height: 1em;
      background-color: ${greyMui._600};
      margin-inline-start: 2px;
      vertical-align: text-bottom;
      animation: blink-cursor 0.8s step-end infinite;
    }

    @keyframes blink-cursor {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0;
      }
    }
  }

  .user-message {
    border-radius: 10px;
    border: 1px solid ${greyMui._300};
    background: ${rydeColors.rowHighlight};
    padding: 8px;
    align-self: flex-end;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin-inline-end: 10px;
  }
`;

export const ChatInputTitleWrapper = styled.span.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "chat-input-title-wrapper",
})`
  background: ${rydeColors.white};
  color: ${rydeColors.darkBlue};
  padding-inline: 4px;
  display: inline-block;

  &.outline {
    transform: translate(10px, -50%);
  }
`;

export const ChatInputWrapper = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ai-message-input-wrapper",
})`
  margin-top: 8px;
  margin-inline-end: 24px;
  padding: 8px 12px;
  border-radius: 5px;
  border: 1px solid #cfd8dc;
  background: #fff;
  display: flex;
  align-items: flex-end;
  gap: 12px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin-inline-end: 10px;
  }

  svg.circle-x-mark {
    color: ${blueGreyMui.A200};
  }

  &.field__error {
    border-color: ${redMui._500};
  }

  &:hover:not(.field__error) {
    border-color: ${purpleMui._500};
  }

  .button-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    color: ${rydeColors.white};
    background: ${rydeColors.venetianNights};
    cursor: pointer;

    > svg {
      width: 12px;
      height: 16px;
    }

    &.isDisabled {
      background-color: #ccc;
      color: ${rydeColors.googleAttributes};
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      pointer-events: none;
      cursor: unset;
    }

    &.isRtl {
      transform: rotate(180deg);
    }
  }
`;

export const ChatTextFieldStyled = styled(TextField)`
  &.MuiFormControl-root {
    width: 100%;
  }

  & .MuiOutlinedInput-root {
    padding: 0;
  }

  & .MuiOutlinedInput-inputMultiline {
    padding: 0;
  }

  & .MuiOutlinedInput-input {
    padding: 0;
    color: ${rydeColors.grey};
  }

  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  & .MuiInputBase-adornedEnd {
    padding-inline-start: 0;
  }

  & .MuiInputAdornment-root {
    margin-block-start: 2px;
  }
`;

export const DetailsLinkStyled = styled.span.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ai-advisor-details-link",
})`
  align-self: flex-end;
  margin-top: 4px;
  margin-inline-end: 0;
  font-size: 13px;
  color: ${greyMui._600};
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: ${purpleMui._600};
  }

  .details-link-arrow {
    margin-inline-start: 2px;
    font-size: 11px;
    opacity: 0.85;
    font-weight: 600;
  }
`;

export const TechnicalDetailsWrapper = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ai-advisor-technical-details",
})`
  margin-top: 16px;
  margin-inline-end: 24px;
  border: 1px solid ${greyMui._300};
  border-radius: 5px;
  background: ${rydeColors.rowHighlight};
  padding: 12px;
  max-height: 200px;
  min-height: 80px;
  overflow: auto;
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin-inline-end: 10px;
  }
`;

export const TraceContentStyled = styled.pre.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ai-advisor-trace-content",
})`
  margin: 0 0 20px 0;
  padding: 12px 16px;
  font-family:
    ui-monospace, "Cascadia Code", "Source Code Pro", Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
  color: ${greyMui._800};
  background: ${rydeColors.white};
  border-radius: 4px;
  border: 1px solid ${greyMui._200};

  .trace-key,
  .keyword-primary {
    color: ${purpleMui._700};
    font-weight: 600;
  }
  .trace-string,
  .keyword-string {
    color: ${blueGreyMui._800};
  }
  .trace-number,
  .keyword-number {
    color: ${blueGreyMui._600};
  }
  .keyword-emphasis {
    color: ${redMui._600};
    font-weight: 600;
  }
  .keyword-thought {
    color: ${blueMui._700};
    font-weight: 600;
  }
  .keyword-error {
    color: ${redMui._900};
    font-weight: 600;
    background-color: ${redMui._50};
    padding: 1px 4px;
    border-radius: 3px;
  }
  .keyword-success {
    color: ${greenMui._900};
    font-weight: 600;
    background-color: ${greenMui._50};
    padding: 1px 4px;
    border-radius: 3px;
  }
  .keyword-technical {
    color: ${blueGreyMui._700};
    font-weight: 500;
  }
  .keyword-action {
    color: ${purpleMui._500};
  }
  .keyword-boolean {
    color: ${blueGreyMui._700};
    font-style: italic;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 8px 12px;
    font-size: 11px;
  }
`;

export const TraceDetailsInnerWrapper = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ai-advisor-trace-inner",
})`
  padding: 10px 10px 40px 20px;
  padding-inline-start: 20px;
  line-height: 1.6;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

/* ===================== AI UI elements ===================== */

export const ChatElementsWrapper = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ai-advisor-chat-elements",
})`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

export const ChatElementGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
`;

export const ChatChoiceButton = styled.button.attrs<IStyledAttributes>({
  type: "button",
  [DATA_E2E_KEY]: "ai-advisor-choice-option",
})<{ $selected?: boolean }>`
  font: inherit;
  font-size: 13px;
  padding: 5px 12px;
  border-radius: 14px;
  cursor: pointer;
  /* The chat is bidirectional; a button label must isolate from the surrounding run. */
  unicode-bidi: isolate;
  border: 1px solid
    ${({ $selected }) => ($selected ? purpleMui._500 : rydeColors.softGray)};
  background: ${({ $selected }) => ($selected ? purpleMui._50 : rydeColors.white)};
  color: ${({ $selected }) => ($selected ? purpleMui._700 : greyMui._800)};

  &:hover:not(:disabled) {
    border-color: ${purpleMui._400};
    color: ${purpleMui._600};
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
`;

export const ChatChoiceSubmit = styled(ChatChoiceButton)`
  border-color: ${purpleMui._500};
  background: ${purpleMui._500};
  color: ${rydeColors.white};

  &:hover:not(:disabled) {
    background: ${purpleMui._600};
    color: ${rydeColors.white};
  }
`;

export const ChatElementLink = styled.a.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ai-advisor-element-link",
})`
  font-size: 13px;
  padding: 5px 12px;
  border-radius: 14px;
  border: 1px solid ${blueMui._200};
  background: ${blueMui._50};
  color: ${blueMui._800};
  text-decoration: none;
  unicode-bidi: isolate;

  &:hover {
    border-color: ${blueMui._400};
    color: ${blueMui._900};
  }
`;
